import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Clock,
  Filter,
  Download,
  Eye,
  X,
  Calendar,
  User,
  Activity,
  Search,
  ChevronLeft,
  ChevronRight,
  List,
  Grid,
  Trash2
} from 'lucide-react';
import './Historial.css';

const Historial = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [selectedLogs, setSelectedLogs] = useState([]); // IDs de logs seleccionados
  const [isAdmin, setIsAdmin] = useState(false);

  // Filtros
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userId: '',
    action: '',
    module: '',
    search: ''
  });

  // Paginación
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Estadísticas
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadActivityLogs();
    loadStats();
    checkUserRole();
  }, [pagination.page, filters]);

  const checkUserRole = () => {
    try {
      const empresaActiva = localStorage.getItem('empresaActiva');
      if (empresaActiva) {
        const empresa = JSON.parse(empresaActiva);
        const userRole = empresa.role || empresa.rolUsuario;
        setIsAdmin(userRole === 'admin');
      }
    } catch (error) {
      console.error('Error al verificar rol:', error);
      setIsAdmin(false);
    }
  };

  const loadActivityLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const empresaActiva = localStorage.getItem('empresaActiva');
      const empresaId = empresaActiva ? JSON.parse(empresaActiva).id : null;

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        )
      };

      const response = await axios.get('/api/activity-logs', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-empresa-id': empresaId
        },
        params
      });

      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error al cargar historial:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const empresaActiva = localStorage.getItem('empresaActiva');
      const empresaId = empresaActiva ? JSON.parse(empresaActiva).id : null;

      const response = await axios.get('/api/activity-logs/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-empresa-id': empresaId
        }
      });

      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset page on filter change
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      userId: '',
      action: '',
      module: '',
      search: ''
    });
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const empresaActiva = localStorage.getItem('empresaActiva');
      const empresaId = empresaActiva ? JSON.parse(empresaActiva).id : null;

      const response = await axios.get('/api/activity-logs/export', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-empresa-id': empresaId
        },
        params: filters,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `historial_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error al exportar:', error);
    }
  };

  const handleDeleteHistory = async () => {
    const confirmDelete = window.confirm(
      '¿Estás seguro de que deseas eliminar TODO el historial de actividades?\n\n' +
      'Esta acción NO se puede deshacer. Se eliminarán todos los registros de actividad.\n\n' +
      '⚠️ Esta opción es solo para pruebas.'
    );

    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem('token');
      const empresaActiva = localStorage.getItem('empresaActiva');
      const empresaId = empresaActiva ? JSON.parse(empresaActiva).id : null;

      await axios.delete('/api/activity-logs', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-empresa-id': empresaId
        }
      });

      alert('✅ Historial eliminado exitosamente');

      // Recargar logs y stats
      await loadActivityLogs();
      await loadStats();
    } catch (error) {
      console.error('Error al eliminar historial:', error);
      alert('❌ Error al eliminar historial: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLogs.length === 0) {
      alert('Por favor selecciona al menos un registro para eliminar');
      return;
    }

    const confirmDelete = window.confirm(
      `¿Estás seguro de que deseas eliminar ${selectedLogs.length} registro(s) seleccionado(s)?\n\n` +
      'Esta acción NO se puede deshacer.'
    );

    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem('token');
      const empresaActiva = localStorage.getItem('empresaActiva');
      const empresaId = empresaActiva ? JSON.parse(empresaActiva).id : null;

      await axios.delete('/api/activity-logs/bulk', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-empresa-id': empresaId
        },
        data: { ids: selectedLogs }
      });

      alert(`✅ ${selectedLogs.length} registro(s) eliminado(s) exitosamente`);
      setSelectedLogs([]);

      // Recargar logs y stats
      await loadActivityLogs();
      await loadStats();
    } catch (error) {
      console.error('Error al eliminar registros:', error);
      alert('❌ Error al eliminar: ' + (error.response?.data?.message || error.message));
    }
  };

  const toggleSelectLog = (logId) => {
    setSelectedLogs(prev => {
      if (prev.includes(logId)) {
        return prev.filter(id => id !== logId);
      } else {
        return [...prev, logId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedLogs.length === logs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(logs.map(log => log.id));
    }
  };

  const viewDetails = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const goToUserInTeam = (userEmail) => {
    // Navegar a Equipo con el email del usuario como parámetro
    navigate('/equipo', { state: { highlightUserEmail: userEmail } });
  };

  const getActionBadgeClass = (action) => {
    const classes = {
      'CREATE': 'badge-success',
      'UPDATE': 'badge-warning',
      'DELETE': 'badge-danger',
      'LOGIN': 'badge-info',
      'LOGOUT': 'badge-secondary',
      'IMPORT': 'badge-primary',
      'EXPORT': 'badge-primary'
    };
    return classes[action] || 'badge-default';
  };

  const getActionText = (action) => {
    const texts = {
      'CREATE': 'Creó',
      'UPDATE': 'Editó',
      'DELETE': 'Eliminó',
      'LOGIN': 'Inició sesión',
      'LOGOUT': 'Cerró sesión',
      'IMPORT': 'Importó',
      'EXPORT': 'Exportó'
    };
    return texts[action] || action;
  };

  const translateEntityType = (entityType) => {
    const translations = {
      'MaestroSupply': 'Maestro Supply',
      'CompanyConfig': 'Configuración Empresa',
      'CompanyLogo': 'Logo Empresa',
      'Client': 'Cliente',
      'Supplier': 'Proveedor',
      'Contact': 'Contacto',
      'TeamMember': 'Miembro Equipo',
      'User': 'Usuario'
    };
    return translations[entityType] || entityType;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatValueForDisplay = (value, convertBooleans = true) => {
    if (!value) return null;

    // Si es un objeto, extraer solo los campos relevantes
    const relevantFields = {
      legalName: 'Razón Social',
      tradeName: 'Nombre Comercial',
      taxId: 'RUT',
      email: 'Email',
      phone: 'Teléfono',
      address: 'Dirección',
      city: 'Ciudad',
      isClient: 'Es Cliente',
      isSupplier: 'Es Proveedor',
      firstName: 'Nombre',
      lastName: 'Apellido',
      role: 'Rol',
      nombreFantasia: 'Nombre Fantasía',
      razonSocial: 'Razón Social',
      rut: 'RUT',
      direccion: 'Dirección',
      telefono: 'Teléfono',
      notes: 'Notas'
    };

    const result = {};
    for (const [key, label] of Object.entries(relevantFields)) {
      if (value[key] !== undefined && value[key] !== null && value[key] !== '') {
        let displayValue = value[key];

        // Convertir booleanos y números 0/1 a Sí/No
        if (convertBooleans) {
          if (typeof displayValue === 'boolean') {
            displayValue = displayValue ? 'Sí' : 'No';
          } else if ((key === 'isClient' || key === 'isSupplier') && (displayValue === 0 || displayValue === 1)) {
            displayValue = displayValue === 1 ? 'Sí' : 'No';
          }
        }

        result[key] = { label, value: displayValue };
      }
    }

    return Object.keys(result).length > 0 ? result : null;
  };

  const getCreatedValues = (newValues) => {
    if (!newValues) return null;
    const formatted = formatValueForDisplay(newValues);
    if (!formatted) return null;

    const values = [];
    for (const key in formatted) {
      values.push({
        label: formatted[key].label,
        value: formatted[key].value
      });
    }

    return values.length > 0 ? values : null;
  };

  const getDeletedValues = (oldValues) => {
    if (!oldValues) return null;
    const formatted = formatValueForDisplay(oldValues);
    if (!formatted) return null;

    const values = [];
    for (const key in formatted) {
      values.push({
        label: formatted[key].label,
        value: formatted[key].value
      });
    }

    return values.length > 0 ? values : null;
  };

  const getChanges = (oldValues, newValues) => {
    if (!oldValues || !newValues) return null;

    const oldFormatted = formatValueForDisplay(oldValues);
    const newFormatted = formatValueForDisplay(newValues);

    if (!oldFormatted || !newFormatted) return null;

    const changes = [];

    // Comparar cada campo
    for (const key in newFormatted) {
      const newVal = newFormatted[key];
      const oldVal = oldFormatted[key];

      // Si el campo existe en ambos y son diferentes
      if (oldVal && String(oldVal.value) !== String(newVal.value)) {
        changes.push({
          label: newVal.label,
          oldValue: oldVal.value,
          newValue: newVal.value
        });
      }
      // Si es un campo nuevo
      else if (!oldVal) {
        changes.push({
          label: newVal.label,
          oldValue: '(vacío)',
          newValue: newVal.value
        });
      }
    }

    return changes.length > 0 ? changes : null;
  };

  // Format value for Historial display
  const formatHistorialValue = (value, fieldLabel) => {
    if (value === null || value === undefined || value === '') return '-';

    // Campos monetarios (con formato chileno: $1.234.567)
    const moneyFields = ['P.U. CLP', 'Total Neto', 'P.U. Proveedor', 'Total Proveedor', 'Utilidad', 'Utilidad Factor', 'Pronto Pago'];
    if (moneyFields.some(f => fieldLabel?.includes(f))) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `$${Math.round(num).toLocaleString('es-CL')}`;
      }
    }

    // Cantidad (sin decimales si es entero)
    if (fieldLabel?.includes('Cantidad')) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num % 1 === 0 ? String(Math.round(num)) : num.toFixed(2).replace('.', ',');
      }
    }

    // Fechas (ya deberían venir en formato correcto)
    return String(value);
  };

  // Parse changes from description (for Maestro Supply bulk edits)
  const parseChangesFromDescription = (description) => {
    if (!description) return null;

    // Check if description contains multiple changes in format: "field: 'old' → 'new', field2: 'old' → 'new'"
    const changePattern = /([^:,]+):\s*'([^']+)'\s*→\s*'([^']+)'/g;
    const matches = [...description.matchAll(changePattern)];

    if (matches.length === 0) return null;

    return matches.map(match => ({
      label: match[1].trim(),
      oldValue: formatHistorialValue(match[2], match[1].trim()),
      newValue: formatHistorialValue(match[3], match[1].trim())
    }));
  };

  return (
    <>
      <div className="historial-container">
      <div className="historial-header">
        <div className="header-left">
          <Clock size={32} className="header-icon" />
          <div>
            <h1>Historial de Actividades</h1>
            <p>Registro completo de acciones realizadas en el sistema</p>
          </div>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button
              className={`btn-toggle ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista Tabla"
            >
              <List size={18} />
            </button>
            <button
              className={`btn-toggle ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Vista Cards"
            >
              <Grid size={18} />
            </button>
          </div>
          <button
            className="btn-filter"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            Filtros
          </button>
          <button
            className="btn-export"
            onClick={handleExport}
          >
            <Download size={18} />
            Exportar
          </button>
          {isAdmin && selectedLogs.length > 0 && (
            <button
              className="btn-delete-selected"
              onClick={handleDeleteSelected}
              title="Eliminar registros seleccionados"
            >
              <Trash2 size={18} />
              Eliminar {selectedLogs.length} seleccionado(s)
            </button>
          )}
          {isAdmin && (
            <button
              className="btn-delete-history"
              onClick={handleDeleteHistory}
              title="Eliminar todo el historial (solo admin)"
            >
              <Trash2 size={18} />
              Eliminar Todo
            </button>
          )}
        </div>
      </div>

      {/* Estadísticas */}
      {stats && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <Activity />
              </div>
              <div className="stat-info">
                <h3>{stats.totalActivities}</h3>
                <p>Total Actividades</p>
              </div>
            </div>
            {stats.actionBreakdown && (
              <>
                <div className="stat-card">
                  <div className="stat-icon stat-icon-create">
                    <Activity />
                  </div>
                  <div className="stat-info">
                    <h3>{stats.actionBreakdown.CREATE || 0}</h3>
                    <p>Creaciones</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon stat-icon-update">
                    <Activity />
                  </div>
                  <div className="stat-info">
                    <h3>{stats.actionBreakdown.UPDATE || 0}</h3>
                    <p>Ediciones</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon stat-icon-delete">
                    <Activity />
                  </div>
                  <div className="stat-info">
                    <h3>{stats.actionBreakdown.DELETE || 0}</h3>
                    <p>Eliminaciones</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon stat-icon-import">
                    <Activity />
                  </div>
                  <div className="stat-info">
                    <h3>{stats.actionBreakdown.IMPORT || 0}</h3>
                    <p>Importaciones</p>
                  </div>
                </div>
              </>
            )}
          </div>
          {stats.topUsers && stats.topUsers.length > 0 && (
            <div className="stats-grid-single">
              <div className="stat-card">
                <div className="stat-icon">
                  <User />
                </div>
                <div className="stat-info">
                  <h3 className="stat-email">{stats.topUsers[0].userName}</h3>
                  <p>Usuario Más Activo</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Filtros */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-field">
              <label>Fecha Inicio</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="filter-field">
              <label>Fecha Fin</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div className="filter-field">
              <label>Acción</label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <option value="">Todas</option>
                <option value="CREATE">Crear</option>
                <option value="UPDATE">Editar</option>
                <option value="DELETE">Eliminar</option>
                <option value="LOGIN">Login</option>
                <option value="LOGOUT">Logout</option>
                <option value="IMPORT">Importar</option>
                <option value="EXPORT">Exportar</option>
              </select>
            </div>
            <div className="filter-field">
              <label>Módulo</label>
              <select
                value={filters.module}
                onChange={(e) => handleFilterChange('module', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="MAESTRO">Maestro</option>
                <option value="CLIENTES">Clientes</option>
                <option value="PROVEEDORES">Proveedores</option>
                <option value="EQUIPO">Equipo</option>
                <option value="EMPRESA">Empresa</option>
                <option value="SISTEMA">Sistema</option>
              </select>
            </div>
            <div className="filter-field filter-field-full">
              <label>Buscar</label>
              <div className="search-input">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Buscar en descripción..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="filters-actions">
            <button className="btn-clear" onClick={clearFilters}>
              Limpiar Filtros
            </button>
          </div>
        </div>
      )}

      {/* Tabla o Cards de Logs */}
      <div className="logs-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando historial...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <Clock size={48} />
            <h3>No hay actividades registradas</h3>
            <p>Los registros aparecerán aquí cuando se realicen acciones en el sistema</p>
          </div>
        ) : viewMode === 'table' ? (
          <>
            <div className="logs-table-container">
              <table className="logs-table">
              <thead>
                <tr>
                  {isAdmin && (
                    <th style={{ width: '50px' }}>
                      <input
                        type="checkbox"
                        checked={selectedLogs.length === logs.length && logs.length > 0}
                        onChange={toggleSelectAll}
                        title="Seleccionar todos"
                      />
                    </th>
                  )}
                  <th>Fecha/Hora</th>
                  <th>Usuario</th>
                  <th>Módulo</th>
                  <th>Acción</th>
                  <th>Descripción</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className={selectedLogs.includes(log.id) ? 'row-selected' : ''}>
                    {isAdmin && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedLogs.includes(log.id)}
                          onChange={() => toggleSelectLog(log.id)}
                        />
                      </td>
                    )}
                    <td className="col-date">{formatDate(log.created_at || log.createdAt)}</td>
                    <td className="col-user">
                      <div className="user-info">
                        <div className="user-avatar">
                          <span>{getInitials(log.userName)}</span>
                        </div>
                        <span>{log.userName}</span>
                      </div>
                    </td>
                    <td>
                      <span className="module-badge">{log.module}</span>
                    </td>
                    <td>
                      <span className={`action-badge ${getActionBadgeClass(log.action)}`}>
                        {getActionText(log.action)}
                      </span>
                    </td>
                    <td className="col-description">{log.description}</td>
                    <td>
                      <button
                        className="btn-view"
                        onClick={() => viewDetails(log)}
                        title="Ver detalles"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Paginación */}
            <div className="pagination">
              <button
                className="btn-page"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                <ChevronLeft size={18} />
                Anterior
              </button>
              <span className="page-info">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <button
                className="btn-page"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
              >
                Siguiente
                <ChevronRight size={18} />
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Vista Cards */}
            <div className="logs-cards-grid">
              {logs.map((log) => (
                <div key={log.id} className={`log-card ${selectedLogs.includes(log.id) ? 'card-selected' : ''}`}>
                  {isAdmin && (
                    <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>
                      <input
                        type="checkbox"
                        checked={selectedLogs.includes(log.id)}
                        onChange={() => toggleSelectLog(log.id)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                    </div>
                  )}
                  <div className="log-card-header">
                    {/* Avatar Circular Clickeable */}
                    <div
                      className="user-avatar-clickable"
                      onClick={() => goToUserInTeam(log.userEmail)}
                      title={`Ir a ${log.userName} en Equipo`}
                      style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '50%',
                        background: 'var(--theme-primary, #c4fffc)',
                        border: '2px solid #000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: 700,
                        color: '#000',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {getInitials(log.userName)}
                    </div>

                    {/* Info Usuario */}
                    <div style={{ flex: 1, marginLeft: '12px' }}>
                      <div style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#000',
                        marginBottom: '4px',
                        fontFamily: 'Space Grotesk, sans-serif'
                      }}>
                        {log.userName}
                      </div>
                      <div style={{
                        display: 'block',
                        fontSize: '12px',
                        color: '#374151',
                        marginBottom: '4px'
                      }}>
                        {formatDate(log.created_at || log.createdAt)}
                      </div>
                      {log.ipAddress && (
                        <div style={{
                          display: 'inline-block',
                          fontSize: '11px',
                          padding: '3px 8px',
                          background: '#f3f4f6',
                          borderRadius: '4px',
                          border: '1px solid #e5e7eb',
                          color: '#6b7280',
                          fontFamily: 'Courier New, monospace'
                        }}>
                          {log.ipAddress}
                        </div>
                      )}
                    </div>
                    <button
                      className="btn-view"
                      onClick={() => viewDetails(log)}
                      title="Ver detalles"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                  <div className="log-card-body">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                      <span className="module-badge">
                        {log.module}
                      </span>
                      <span className={`action-badge ${getActionBadgeClass(log.action)}`}>
                        {getActionText(log.action)}
                      </span>
                    </div>
                    <div className="log-card-description">{log.description}</div>
                    {log.entityType && (
                      <div className="log-card-meta">
                        <span className="log-card-meta-label">Tipo:</span>
                        <span className="log-card-meta-value">{translateEntityType(log.entityType)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Paginación */}
            <div className="pagination">
              <button
                className="btn-page"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                <ChevronLeft size={18} />
                Anterior
              </button>
              <span className="page-info">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <button
                className="btn-page"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
              >
                Siguiente
                <ChevronRight size={18} />
              </button>
            </div>
          </>
        )}
      </div>
      </div>

      {/* Modal de Detalles - Renderizado con Portal fuera del DOM principal */}
      {showDetailModal && selectedLog && ReactDOM.createPortal(
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Detalle de Actividad</h2>
              <button
                className="btn-close-modal"
                onClick={() => setShowDetailModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-summary">
                <div className="summary-header">
                  <div className="user-avatar-large">
                    <span>{getInitials(selectedLog.userName)}</span>
                  </div>
                  <div className="summary-info">
                    <h3>{selectedLog.userName}</h3>
                    <p>{selectedLog.userEmail}</p>
                  </div>
                </div>
                <div className="summary-action">
                  <span className={`action-badge ${getActionBadgeClass(selectedLog.action)}`}>
                    {getActionText(selectedLog.action)}
                  </span>
                  <span className="module-badge">{selectedLog.module}</span>
                </div>
              </div>

              <div className="detail-grid">
                <div className="detail-item">
                  <label>🕐 Fecha y Hora</label>
                  <p>{formatDate(selectedLog.created_at || selectedLog.createdAt)}</p>
                </div>
                <div className="detail-item">
                  <label>🌐 Dirección IP</label>
                  <p>{selectedLog.ipAddress || 'No disponible'}</p>
                </div>
                <div className="detail-item detail-item-full">
                  <label>📝 Qué se hizo</label>
                  <p>{selectedLog.description}</p>
                </div>
              </div>

              {/* Sección de valores según la acción */}
              {selectedLog.action === 'CREATE' && getCreatedValues(selectedLog.newValues) && (
                <div className="changes-section">
                  <h3>✨ Datos Creados</h3>
                  <div className="values-list">
                    {getCreatedValues(selectedLog.newValues).map((item, idx) => (
                      <div key={idx} className="value-item">
                        <div className="value-label">{item.label}</div>
                        <div className="value-text">{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedLog.action === 'UPDATE' && (getChanges(selectedLog.oldValues, selectedLog.newValues) || parseChangesFromDescription(selectedLog.description)) && (
                <div className="changes-section">
                  <h3>🔄 Cambios Realizados</h3>
                  <div className="changes-list">
                    {(getChanges(selectedLog.oldValues, selectedLog.newValues) || parseChangesFromDescription(selectedLog.description)).map((change, idx) => (
                      <div key={idx} className="change-item">
                        <div className="change-label">{change.label}</div>
                        <div className="change-comparison">
                          <div className="change-old">
                            <span className="change-tag">Antes</span>
                            <span className="change-value">{change.oldValue}</span>
                          </div>
                          <div className="change-arrow">→</div>
                          <div className="change-new">
                            <span className="change-tag">Ahora</span>
                            <span className="change-value">{change.newValue}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedLog.action === 'DELETE' && getDeletedValues(selectedLog.oldValues) && (
                <div className="changes-section">
                  <h3>🗑️ Datos Eliminados</h3>
                  <div className="values-list deleted-values">
                    {getDeletedValues(selectedLog.oldValues).map((item, idx) => (
                      <div key={idx} className="value-item">
                        <div className="value-label">{item.label}</div>
                        <div className="value-text">{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Historial;
